import Imgix from './Imgix';
import Swiper from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';

export default class LightboxGallery {
    constructor() {
        this.imgixInstance = new Imgix(); // register new Imgix instance

        this.currentMedia = [];
        this.imagesRequested = false;
        this.lightboxContainer = document.getElementById('lightbox-gallery');
        this.swiper = null;

        this.init();
    }

    init() {
        this.lightboxContainer.querySelector('.c-lightbox__close-button').addEventListener('click', () => this.closeLightbox());

        this.lightboxContainer.addEventListener('click', (event) => {
            const rect = this.lightboxContainer.getBoundingClientRect();
            const isInDialog = (event.clientX >= rect.left && event.clientX <= rect.right &&
                                event.clientY >= rect.top && event.clientY <= rect.bottom);
            if (!isInDialog) this.closeLightbox();
        });

        document.body.addEventListener('keydown', (e) => {
            if (e.key === "Escape") this.closeLightbox();
        });

        document.querySelectorAll('[data-lightbox="gallery"]').forEach((gallery) => {
            const mediaElements = gallery.querySelectorAll('img, video'),
                buttonTrigger = gallery.closest('figure')?.querySelector('[data-lightbox="trigger"]');

            mediaElements.forEach((media) => {
                media.addEventListener('click', (e) => this.openLightbox(gallery, e.target));
            });

            buttonTrigger?.addEventListener('click', () => {
                const activeImage = gallery.querySelector('.swiper-slide-active > *');
                const targetImage = activeImage || mediaElements[0];
                this.openLightbox(gallery, targetImage);
            });
        });
    }

    openLightbox(gallery, clickedMedia) {
        let skipped = false;
        // Filter only visible media (non .is-inactive)
        let mediaElements = Array.from(gallery.querySelectorAll('img, video'))
            .filter(media => {
                // Skip if inside .is-inactive
                if (media.closest('.is-inactive')) return false;

                // Skip the first media inside .c-cta--stack-images
                if (!skipped && media.closest('.c-cta--stack-images')) {
                    skipped = true;
                    return false;
                }

                return true;
            });
        // Re-assign data-gallery-index to only visible media
        mediaElements.forEach((media, index) => {
            media.setAttribute('data-gallery-index', index);
        });

        // Sort media based on new data-gallery-index
        mediaElements = mediaElements.sort((a, b) =>
            parseInt(a.getAttribute('data-gallery-index'), 10) -
            parseInt(b.getAttribute('data-gallery-index'), 10)
        );

        // Store current visible media for rendering
        this.currentMedia = mediaElements.map((media) => ({
            src: media.dataset.src || media.src,
            type: media.tagName.toLowerCase(),
        }));

        // Render the lightbox with visible media
        this.renderLightbox();

        // Get index of clicked media within filtered list
        const clickedIndex = mediaElements.findIndex(media => media === clickedMedia);

        this.swiper.slideToLoop(clickedIndex >= 0 ? clickedIndex : 0, 0);

        this.lightboxContainer.showModal();
        document.body.classList.add('lightbox-open');
    }

    renderLightbox() {
        // Initialize or reinitialize Swiper with proper options
        if (this.swiper) {
            this.swiper.destroy(true, true);
        }

        const swiperWrapper = this.lightboxContainer.querySelector('.swiper-wrapper');
        swiperWrapper.innerHTML = '';

        this.currentMedia.forEach(({ src, type }) => {
            const slide = document.createElement('div');
            slide.classList.add('swiper-slide');

            if (type === 'img') {
                slide.innerHTML = `<img class="imgix-fluid / img-fit o-image" data-src="${src}" />`;
            } else if (type === 'video') {
                slide.innerHTML = `<video class="lightbox-video" controls autoplay muted><source src="${src}" type="video/mp4"></video>`;
            }

            swiperWrapper.appendChild(slide);
        });

        this.swiper = new Swiper('.swiper', {
            modules: [Navigation, Pagination],
            loop: true,
            slidesPerView: 1,
            loopedSlides: this.currentMedia.length,
            spaceBetween: 0,
            pagination: {
                el: '.swiper-pagination',
                type: 'fraction'
            },
            navigation: {
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev',
            },
            on: {
                afterInit: (swiper) => {
                    if (this.imagesRequested) return;

                    setTimeout(() => {
                        document.dispatchEvent(new CustomEvent("ajaxModalHasLoaded"));
                    }, 150);

                    this.imagesRequested = true;
                    this.handleVideoPlayback(swiper);
                },
                slideChange: (swiper) => {
                    this.handleVideoPlayback(swiper);
                },
                slideChangeTransitionEnd: (swiper) => {
                    this.handleVideoPlayback(swiper);
                },
            },
        });
    }

    closeLightbox() {
        document.body.classList.remove('lightbox-open');
        this.lightboxContainer.close(); // Use the close() method to close the dialog
        this.imagesRequested = false;
        this.swiper.destroy(true, true); // Destroy Swiper to reset it when the lightbox closes
    }

    // Handle video playback on slide change
    handleVideoPlayback(swiper) {
        // Check if the lightbox is open
        if (!document.body.classList.contains('lightbox-open')) {
            // Pause all videos if the lightbox is not open
            this.pauseAllVideos(swiper);
            return; // Exit the function if the lightbox is closed
        }

        const currentSlide = swiper.slides[swiper.activeIndex];
        if (!currentSlide) return;

        const videoElement = currentSlide.querySelector('video');
        this.pauseAllVideos(swiper, videoElement);

        // Ensure video is not already playing before attempting to play it
        if (videoElement && videoElement.paused) {
            requestAnimationFrame(() => {
                videoElement.play().catch((error) => {
                    console.warn("Video play() interrupted:", error);
                });
            });
        }
    }

    // Helper function to pause all videos except the provided one (optional)
    pauseAllVideos(swiper, excludeVideo = null) {
        swiper.slides.forEach(slide => {
            const video = slide.querySelector('video');
            if (video && video !== excludeVideo) {
                video.pause();
            }
        });
    }
}
